import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  IconButton,
  Collapse,
  Popover,
  Button,
  Stack,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VerifiedIcon from '@mui/icons-material/Verified';
import RecommendIcon from '@mui/icons-material/Recommend';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FolderIcon from '@mui/icons-material/Folder';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TuneIcon from '@mui/icons-material/Tune';
// QueryStatsIcon removed (lot B step 12: measurement sidebar item hidden)
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useEcommerceTour } from '../../hooks/useEcommerceTour';
import type { FeatureFlags, TierLimits } from '@traaker/shared';
import { resolveLimits, canAccessReports } from '@traaker/shared';

type NavItem = {
  path: string;
  /** i18n key — resolved with `t()` at render (labels live in module scope). */
  labelKey: string;
  icon: React.ReactNode;
  requireFeature?: keyof FeatureFlags;
  /** Hide the item when this numeric tier limit resolves to 0. Quota is the
   *  scoping primitive for Competitors (plan §10.1): competitors===0 ⇒ hidden. */
  requireQuota?: keyof TierLimits;
  /** Reports access rule: agency always, enterprise via reportsEnabled flag. */
  requireReports?: boolean;
  /** Optional badge rendered as a small diagonal ribbon on the item.
   *  'new' shows a « NEW » pill. */
  badge?: 'new';
};
type NavSection = {
  titleKey: string;
  items: NavItem[];
  /** Renders an info popover button next to the section title. */
  hasInfo?: boolean;
  /** Section header toggles the items; collapsed by default unless the
   *  current route is one of its items. */
  collapsible?: boolean;
};

/** Announcement post behind the E-Commerce section's info popover. */
const ECOMMERCE_ANNOUNCEMENT_URL =
  'https://traaker.ai/fr/blog/traaker-lance-le-module-ecommerce/';

const OVERVIEW_ITEM: NavItem = { path: '/', labelKey: 'sidebar.overview', icon: <DashboardIcon /> };

const NAV_SECTIONS: NavSection[] = [
  {
    titleKey: 'sidebar.section.strategy',
    items: [
      { path: '/strategy', labelKey: 'sidebar.brandKnowledge', icon: <AccountTreeIcon /> },
      { path: '/geo-strategy', labelKey: 'sidebar.geoStrategy', icon: <AutoAwesomeIcon /> },
    ],
  },
  {
    titleKey: 'sidebar.section.audit',
    items: [
      { path: '/audits', labelKey: 'sidebar.websiteAudit', icon: <SearchIcon /> },
      { path: '/earned-media', labelKey: 'sidebar.earnedMedia', icon: <NewspaperIcon /> },
      { path: '/competitors', labelKey: 'sidebar.competitors', icon: <CompareArrowsIcon />, requireQuota: 'competitors' },
    ],
  },
  {
    titleKey: 'sidebar.section.actions',
    items: [
      { path: '/tasks', labelKey: 'sidebar.tasks', icon: <TaskAltIcon />, requireFeature: 'tasksEnabled' },
      { path: '/news-watch', labelKey: 'sidebar.studio', icon: <EditNoteIcon />, requireFeature: 'newsWatchEnabled' },
    ],
  },
  {
    titleKey: 'sidebar.section.aiVisibility',
    items: [
      { path: '/brand/narrative', labelKey: 'sidebar.brandImage', icon: <MenuBookIcon /> },
      { path: '/brand/comparison', labelKey: 'sidebar.comparison', icon: <CompareArrowsIcon /> },
      { path: '/brand/referral', labelKey: 'sidebar.recommendations', icon: <RecommendIcon /> },
      { path: '/brand/authority', labelKey: 'sidebar.authority', icon: <VerifiedIcon /> },
      {
        path: '/brand/conversations',
        labelKey: 'sidebar.conversations',
        icon: <RecordVoiceOverOutlinedIcon />,
        requireFeature: 'conversationsEnabled',
        requireQuota: 'conversation_personas',
        badge: 'new',
      },
    ],
  },
  {
    // Retailer-only Product module — every item gated on productModuleEnabled,
    // so the whole section auto-hides for non-retail tenants (docs/features/retailer-catalog.md).
    titleKey: 'sidebar.section.product',
    hasInfo: true,
    collapsible: true,
    items: [
      { path: '/product/catalog', labelKey: 'sidebar.catalog', icon: <Inventory2Icon />, requireFeature: 'productModuleEnabled' },
      { path: '/product/optimization', labelKey: 'sidebar.productOptimization', icon: <TuneIcon />, requireFeature: 'productModuleEnabled' },
      // lot B step 12 / C17.1 U2: measurement UI hidden (route redirects to catalog)
      { path: '/product/content', labelKey: 'sidebar.productContent', icon: <ShoppingBagIcon />, requireFeature: 'productModuleEnabled' },
    ],
  },
  {
    titleKey: 'sidebar.section.reports',
    items: [
      { path: '/reports/library', labelKey: 'sidebar.myReports', icon: <FolderIcon />, requireReports: true },
      { path: '/reports/audit', labelKey: 'sidebar.reportTemplate', icon: <AssessmentIcon />, requireReports: true },
    ],
  },
];

interface SidebarProps {
  width: number;
}

export default function Sidebar({ width }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const [newsAnchor, setNewsAnchor] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const startEcommerceTour = useEcommerceTour();
  const flags = tenant?.settings?.featureFlags;
  const limits = tenant ? resolveLimits(tenant.tier, tenant.settings?.quotaOverrides) : null;
  const itemVisible = (item: NavItem) => {
    if (item.requireFeature && flags?.[item.requireFeature] !== true) return false;
    // Quota gate: a numeric limit of 0 hides the item (−1 = unlimited shows).
    if (item.requireQuota) {
      const q = limits?.[item.requireQuota];
      if (typeof q === 'number' && q === 0) return false;
    }
    // Reports gate: agency always, enterprise only with the reportsEnabled flag.
    if (item.requireReports && !canAccessReports(tenant?.tier, flags?.reportsEnabled)) {
      return false;
    }
    return true;
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Billing is the settings `billing` tab surfaced as its own nav entry.
  const onBillingTab =
    location.pathname === '/settings' &&
    new URLSearchParams(location.search).get('tab') === 'billing';

  const renderItem = (item: NavItem) => {
    const active = isActive(item.path);
    return (
      <ListItemButton
        key={item.path}
        data-tour={`nav:${item.path}`}
        // The explicit scroll covers the case `ScrollToTop` can't see: clicking the
        // entry for the page you are already on only changes the query string (the
        // Conversations view tab, a `?tab=`), and that component keys on `pathname`
        // so a filter change never yanks the page. A nav click should still land at
        // the top (2026-10-03).
        onClick={() => { window.scrollTo(0, 0); navigate(item.path); }}
        selected={active}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          position: 'relative',
          overflow: 'hidden',
          '&.Mui-selected': {
            bgcolor: 'rgba(124, 58, 237, 0.10)',
            '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.16)' },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={t(item.labelKey)}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: active ? 600 : 500,
          }}
        />
        {item.badge === 'new' && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 6,
              right: -14,
              width: 52,
              textAlign: 'center',
              transform: 'rotate(35deg)',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.5rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              lineHeight: '16px',
              height: 16,
              pointerEvents: 'none',
            }}
          >
            NEW
          </Box>
        )}
      </ListItemButton>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="img"
          src="/traaker-icon.svg"
          alt=""
          sx={{ height: 32, width: 32, objectFit: 'contain', display: 'block' }}
        />
        <Typography
          sx={{
            fontFamily: '"Inter Tight", "Inter", sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            letterSpacing: '0.02em',
            lineHeight: 1,
            textTransform: 'uppercase',
            color: 'text.primary',
          }}
        >
          TR<Box component="span" sx={{ color: 'primary.main' }}>A</Box>AKER
        </Typography>
      </Box>

      <Divider />

      <List sx={{ px: 1, mt: 1 }}>{renderItem(OVERVIEW_ITEM)}</List>

      {NAV_SECTIONS.map((section) => {
        const visibleItems = section.items.filter(itemVisible);
        if (visibleItems.length === 0) return null;
        const open =
          !section.collapsible ||
          (expanded[section.titleKey] ?? visibleItems.some((i) => isActive(i.path)));
        const toggle = () => setExpanded((prev) => ({ ...prev, [section.titleKey]: !open }));
        return (
          <Box key={section.titleKey} sx={{ mt: 1.5 }}>
            <Box
              sx={{
                px: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                ...(section.collapsible && { cursor: 'pointer', userSelect: 'none' }),
              }}
              onClick={section.collapsible ? toggle : undefined}
              role={section.collapsible ? 'button' : undefined}
              aria-expanded={section.collapsible ? open : undefined}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                }}
              >
                {t(section.titleKey)}
              </Typography>
              {section.hasInfo && (
                <IconButton
                  size="small"
                  aria-label={t('sidebar.newInfoAria')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewsAnchor(e.currentTarget);
                  }}
                  sx={{ p: 0.125, color: 'primary.main' }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
              {section.collapsible && (
                <ExpandMoreIcon
                  sx={{
                    ml: 'auto',
                    fontSize: 18,
                    color: 'text.secondary',
                    transition: 'transform 0.2s',
                    transform: open ? 'rotate(180deg)' : 'none',
                  }}
                />
              )}
            </Box>
            <Collapse in={open} timeout="auto">
              <List sx={{ px: 1, pt: 0.5 }}>{visibleItems.map(renderItem)}</List>
            </Collapse>
          </Box>
        );
      })}

      <Popover
        open={Boolean(newsAnchor)}
        anchorEl={newsAnchor}
        onClose={() => setNewsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('sidebar.productNewsTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('sidebar.productNewsBody')}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ExploreOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                setNewsAnchor(null);
                startEcommerceTour();
              }}
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
            >
              {t('sidebar.productNewsTourCta')}
            </Button>
            <Button
              size="small"
              href={ECOMMERCE_ANNOUNCEMENT_URL}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => setNewsAnchor(null)}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('sidebar.productNewsCta')}
            </Button>
          </Stack>
        </Box>
      </Popover>

      <Box sx={{ flexGrow: 1 }} />

      <List sx={{ px: 1, pb: 2 }}>
        <ListItemButton
          data-tour="nav:/settings?tab=billing"
          onClick={() => navigate('/settings?tab=billing')}
          selected={onBillingTab}
          sx={{ borderRadius: 2, mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: onBillingTab ? 'primary.main' : 'text.secondary' }}>
            <CreditCardIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('sidebar.billing')}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: onBillingTab ? 600 : 500 }}
          />
        </ListItemButton>
        <ListItemButton
          data-tour="nav:/settings"
          onClick={() => navigate('/settings')}
          selected={location.pathname === '/settings' && !onBillingTab}
          sx={{ borderRadius: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('sidebar.settings')}
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </ListItemButton>
      </List>
    </Drawer>
  );
}
